import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { Link, useParams } from "react-router-dom";

import { useLearningCatalog, useLearningCoursePayload, useMyLearning } from "../../../hooks/useLearningCatalog";
import { createLearningCommandId, LearningQuizResult, useLearningCommands } from "../../../hooks/useLearningCommands";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { borderRadius, color, shadows, space } from "../../../theme/designTokens";
import { QuizResult } from "./QuizResult";

export const FinalQuiz = () => {
  const translate = useTranslate();
  const { courseId } = useParams<{ courseId: string }>();
  const catalog = useLearningCatalog();
  const learning = useMyLearning();
  const summary = catalog.courses.find(course => course.id === courseId);
  const courseState = learning.courses.find(course => course.courseId === courseId);
  const activeVersion =
    courseState?.status === "IN_PROGRESS" && courseState.startedVersions.length > 0
      ? courseState.startedVersions[courseState.startedVersions.length - 1]
      : summary?.version;
  const payload = useLearningCoursePayload(summary?.id, learning.loading ? undefined : activeVersion);
  const { submitQuiz, submitting } = useLearningCommands();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [attemptId, setAttemptId] = useState(() => createLearningCommandId());
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString());
  const [result, setResult] = useState<LearningQuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submissionInput = useRef<Parameters<typeof submitQuiz>[0] | null>(null);

  const course = payload.course;
  const allLessonsComplete = Boolean(
    course && course.lessons.every(lesson => courseState?.completedLessonIds.includes(lesson.id))
  );

  if (catalog.loading || learning.loading || payload.loading) {
    return (
      <Box display="grid" minHeight={320} sx={{ placeItems: "center" }}>
        <CircularProgress aria-label={translate("learning.quiz.loading")} />
      </Box>
    );
  }
  if (catalog.error || learning.error || payload.error) {
    return <Alert severity="error">{translate("learning.course.contentError")}</Alert>;
  }
  if (!summary || summary.locked || !courseState || !course) {
    return <Alert severity="warning">{translate("learning.player.notAvailable")}</Alert>;
  }
  if (!allLessonsComplete) {
    const firstIncomplete = course.lessons.find(lesson => !courseState.completedLessonIds.includes(lesson.id));
    return (
      <Alert
        severity="info"
        action={
          firstIncomplete ? (
            <Button component={Link} to={`/learning/courses/${course.id}/lessons/${firstIncomplete.id}`}>
              {translate("learning.quiz.continueLessons")}
            </Button>
          ) : undefined
        }
      >
        {translate("learning.quiz.lessonsRequired")}
      </Alert>
    );
  }

  const retry = (): void => {
    setAnswers({});
    setAttemptId(createLearningCommandId());
    setStartedAt(new Date().toISOString());
    submissionInput.current = null;
    setResult(null);
    setError(null);
  };

  if (result) return <QuizResult result={result} onRetry={retry} />;

  const questions = course.finalQuiz.questions;
  const allAnswered = questions.every(question => Boolean(answers[question.id]));

  const submit = async (): Promise<void> => {
    if (!allAnswered) {
      setError(translate("learning.quiz.answerAll"));
      return;
    }
    const correct = questions.filter(
      question => question.answers.find(answer => answer.id === answers[question.id])?.correct
    ).length;
    const clientScorePercent = Math.round((correct / questions.length) * 10000) / 100;
    setError(null);
    try {
      let input = submissionInput.current;
      if (!input) {
        input = {
          clientAttemptId: attemptId,
          courseId: course.id,
          courseVersion: course.version,
          startedAt,
          finishedAt: new Date().toISOString(),
          clientScorePercent,
          clientPassed: clientScorePercent >= course.finalQuiz.passingScorePercent,
          answers: questions.map(question => ({ questionId: question.id, answerId: answers[question.id] })),
        };
        submissionInput.current = input;
      }
      const submitted = await submitQuiz(input);
      setResult(submitted);
    } catch {
      setError(translate("learning.quiz.submitError"));
    }
  };

  return (
    <Stack spacing={space.xl}>
      <Button
        component={Link}
        to={`/learning/courses/${course.id}`}
        startIcon={<ArrowBack />}
        sx={{ alignSelf: "flex-start" }}
      >
        {translate("learning.player.backToCourse")}
      </Button>
      <Box>
        <Typography color="text.secondary">{course.title}</Typography>
        <Typography component="h1" variant="h4">
          {translate("learning.quiz.title")}
        </Typography>
        <Typography color="text.secondary">
          {translate("learning.quiz.passingScore", { score: course.finalQuiz.passingScorePercent })}
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {questions.map((question, index) => (
        <Paper
          component="section"
          key={question.id}
          variant="outlined"
          sx={{
            p: { xs: space.xl, md: space.xxl1 },
            borderRadius: borderRadius.md,
            borderColor: color.panelBorder,
            boxShadow: shadows.card,
          }}
        >
          <FormControl fullWidth>
            <FormLabel component="legend">
              <Typography component="span" variant="h6">
                {translate("learning.quiz.question", { current: index + 1, total: questions.length })}:{" "}
                {question.prompt}
              </Typography>
            </FormLabel>
            {question.image && (
              <Box
                component="img"
                src={resolveLearningMediaUrl(question.image.fn)}
                alt={question.image.alt ?? ""}
                sx={{ width: "100%", maxHeight: 360, objectFit: "cover", my: space.xl, borderRadius: borderRadius.md }}
              />
            )}
            <RadioGroup
              value={answers[question.id] ?? ""}
              onChange={event => setAnswers(previous => ({ ...previous, [question.id]: event.target.value }))}
            >
              {question.answers.map(answer => (
                <FormControlLabel
                  data-testid={`learning-quiz-answer-${question.id}-${answer.id}`}
                  key={answer.id}
                  value={answer.id}
                  control={<Radio />}
                  label={
                    <Stack direction="row" spacing={space.md} alignItems="center">
                      {answer.image && (
                        <Box
                          component="img"
                          src={resolveLearningMediaUrl(answer.image.fn)}
                          alt={answer.image.alt ?? ""}
                          sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: borderRadius.sm }}
                        />
                      )}
                      <Typography>{answer.text}</Typography>
                    </Stack>
                  }
                  sx={{
                    p: space.md,
                    borderRadius: borderRadius.sm,
                    border: `1px solid ${color.panelBorder}`,
                    mb: space.md,
                    ml: 0,
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>
      ))}
      <Button
        data-testid="learning-submit-quiz"
        variant="contained"
        disabled={submitting}
        onClick={() => void submit()}
      >
        {translate("learning.quiz.submit")}
      </Button>
    </Stack>
  );
};
